/**
 * Lightweight reversible cipher for localStorage values.
 * Prevents casual reads/edits of cart & auth JSON in DevTools.
 * Not a substitute for server-side catalog reprice on order submit.
 *
 * Wire format: ooenc1.<base64(salt[8] + mac[8] + ciphertext)>
 * Legacy plain JSON is still accepted on read (one-time migrate on write).
 */

const PREFIX = 'ooenc1.';

function getSecret() {
  const fromEnv = String(
    (typeof process !== 'undefined' && process.env?.REACT_APP_STORAGE_CIPHER_KEY) || ''
  ).trim();
  // App-scoped fallback — override via REACT_APP_STORAGE_CIPHER_KEY in production builds
  return fromEnv || 'serveiq-oo-storage-v1';
}

function utf8Encode(str) {
  return new TextEncoder().encode(String(str));
}

function utf8Decode(bytes) {
  return new TextDecoder().decode(bytes);
}

function bytesToB64(bytes) {
  let bin = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i += 1) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function randomSalt(len = 8) {
  const salt = new Uint8Array(len);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(salt);
  } else {
    for (let i = 0; i < len; i += 1) salt[i] = (Math.random() * 256) | 0;
  }
  return salt;
}

/** FNV-1a 32-bit */
function fnv1a(bytes, seed = 0x811c9dc5) {
  let h = seed >>> 0;
  for (let i = 0; i < bytes.length; i += 1) {
    h ^= bytes[i];
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function expandKeystream(secret, salt, length) {
  const secretBytes = utf8Encode(secret);
  const out = new Uint8Array(length);
  let state = fnv1a(salt, fnv1a(secretBytes));
  let counter = 0;
  for (let i = 0; i < length; i += 1) {
    const block = new Uint8Array(12);
    block.set(salt, 0);
    block[8] = counter & 0xff;
    block[9] = (counter >>> 8) & 0xff;
    block[10] = (counter >>> 16) & 0xff;
    block[11] = (counter >>> 24) & 0xff;
    state = fnv1a(block, state);
    out[i] = state & 0xff;
    counter += 1;
  }
  return out;
}

function xorBytes(data, keystream) {
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i += 1) {
    out[i] = data[i] ^ keystream[i % keystream.length];
  }
  return out;
}

function mac8(secret, salt, cipher) {
  const secretBytes = utf8Encode(secret);
  const buf = new Uint8Array(secretBytes.length + salt.length + cipher.length);
  buf.set(secretBytes, 0);
  buf.set(salt, secretBytes.length);
  buf.set(cipher, secretBytes.length + salt.length);
  let h1 = fnv1a(buf, 0x811c9dc5);
  let h2 = fnv1a(buf, 0x811c9dc5 ^ 0xa5a5a5a5);
  const out = new Uint8Array(8);
  out[0] = h1 & 0xff;
  out[1] = (h1 >>> 8) & 0xff;
  out[2] = (h1 >>> 16) & 0xff;
  out[3] = (h1 >>> 24) & 0xff;
  out[4] = h2 & 0xff;
  out[5] = (h2 >>> 8) & 0xff;
  out[6] = (h2 >>> 16) & 0xff;
  out[7] = (h2 >>> 24) & 0xff;
  return out;
}

function macEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a[i] ^ b[i];
  return diff === 0;
}

export function encryptStorageValue(value, secret = getSecret()) {
  const json = JSON.stringify(value);
  const plain = utf8Encode(json);
  const salt = randomSalt(8);
  const keystream = expandKeystream(secret, salt, plain.length);
  const cipher = xorBytes(plain, keystream);
  const mac = mac8(secret, salt, cipher);
  const packed = new Uint8Array(salt.length + mac.length + cipher.length);
  packed.set(salt, 0);
  packed.set(mac, salt.length);
  packed.set(cipher, salt.length + mac.length);
  return PREFIX + bytesToB64(packed);
}

export function decryptStorageValue(raw, secret = getSecret()) {
  if (raw == null || raw === '') return null;
  const text = String(raw);

  if (!text.startsWith(PREFIX)) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  try {
    const packed = b64ToBytes(text.slice(PREFIX.length));
    if (packed.length < 16) return null;
    const salt = packed.slice(0, 8);
    const mac = packed.slice(8, 16);
    const cipher = packed.slice(16);
    const expected = mac8(secret, salt, cipher);
    if (!macEqual(mac, expected)) return null;
    const keystream = expandKeystream(secret, salt, cipher.length);
    const plain = xorBytes(cipher, keystream);
    return JSON.parse(utf8Decode(plain));
  } catch {
    return null;
  }
}

export function setEncryptedItem(key, value) {
  localStorage.setItem(key, encryptStorageValue(value));
}

export function getEncryptedItem(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const value = decryptStorageValue(raw);
    if (value != null && !String(raw).startsWith(PREFIX)) {
      try {
        setEncryptedItem(key, value);
      } catch {
        /* ignore quota */
      }
    }
    return value;
  } catch {
    return null;
  }
}

export function removeEncryptedItem(key) {
  localStorage.removeItem(key);
}
